import { computed, nextTick, ref, watch } from 'vue'
import { useInvestingInvestorStore } from '../stores/investor-store'
import { helpers, required, sameAs } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'
import { AddOn, Subscription } from '../types'
import { initialMoney, Money, toMoney, toNumber } from '@/modules/shared/utils/money'
import { parse } from '@/modules/shared/utils/v-table'
import { useAuthStore } from '@/modules/auth/stores/auth-store'
import {
  useCatalogStore,
  type CatalogFundPlan,
  type CatalogFundAddOn,
  type FundBillingFrequency,
} from '@/modules/billing/stores/catalog-store'
import { EntityExpanded } from '../stores/better-entity-store'
import { format } from 'date-fns'

////////////////////////////////////////////////////////////////
//// Options
////////////////////////////////////////////////////////////////

const legalStructureOptions = [
  'LLC',
  'LP',
  'LLP',
  'Ltd',
  'Non-Profit Corporation',
  'C-Corp',
  'S-Corp',
  'Pte Ltd',
  'Pty Ltd',
  'Trust',
  'IRA',
  'ROTH IRA',
  'Disregarded Entity',
  'Other',
].map((structure) => ({
  label: structure,
  value: structure,
}))

const isNumber = (value: any) => value === null || value === '' || !isNaN(Number(value))
const mustBeNumber = helpers.withMessage('Must be a number', isNumber)

const generateAdminOptions = (investorStore) =>
  computed(() =>
    investorStore.individuals
      .map((investor) => ({
        label: investor.name,
        value: investor.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  )

const generateManagersOptions = (investorStore) =>
  computed(() =>
    (investorStore.investors || [])
      .map((investor) => ({
        label: investor.name,
        value: investor._cid,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  )

const capitalCallFrequencyOptions = [
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly', value: 'quarterly' },
  { label: 'Semi-Annually', value: 'semi_annually' },
  { label: 'Annually', value: 'annually' },
  { label: 'As Needed', value: 'as_needed' },
]

const ownershipTypeOptions = [
  { label: 'By commitment', value: 'committed' },
  { label: 'By capital invested', value: 'capital_called' },
  { label: 'Custom', value: 'assigned' },
]

const selectedBillingFrequencyOptions = [
  { label: 'Yearly', value: 'year' },
  { label: 'Quarterly', value: 'quarter' },
  { label: 'Monthly', value: 'month' },
]

const investmentTypeOptions = ['Equity', 'Convertible Note', 'SAFE', 'Other'].map((type) => ({
  label: type,
  value: type,
}))

const sectorOptions = [
  'Artificial Intelligence',
  'Biotech / Life Sciences',
  'Climate / Clean Energy',
  'Consumer Products',
  'Cybersecurity',
  'Defense / Aerospace',
  'E-Commerce',
  'EdTech',
  'FinTech',
  'FoodTech',
  'Gaming',
  'Hardware',
  'HealthTech',
  'Infrastructure',
  'InsurTech',
  'Logistics / Supply Chain',
  'Manufacturing',
  'MarketPlace',
  'Media / Entertainment',
  'PropTech / Real Estate',
  'Robotics',
  'SaaS',
  'SpaceTech',
  'Telecom',
  'Transportation',
  'Web3 / Blockchain',
  'Other',
].map((s) => ({ label: s, value: s }))

const visibilityOptions = [
  { label: 'All Investors', value: 'all_investors' },
  { label: 'Selected Investors', value: 'selected' },
]

const investmentStageOptions = ['Pre-Seed', 'Seed', 'Early', 'Growth', 'Pre-IPO', 'Secondaries'].map((s) => ({
  label: s,
  value: s,
}))

// Catalog data synced from the pinia store via watchers in the composable body.
// Module-level refs so the computed options below (which resolve prices against
// `form.value.selected_billing_frequency`) don't depend on a particular store instance.
const _catalogFundPlans = ref<CatalogFundPlan[]>([])
const _catalogFundAddOns = ref<CatalogFundAddOn[]>([])

const subscriptionOptions = computed((): Subscription[] => {
  const frequency = form.value.selected_billing_frequency as FundBillingFrequency
  return _catalogFundPlans.value
    .filter((plan) => plan.current)
    .map((plan) => ({
      title: plan.title,
      value: plan.stripe_name,
      included: plan.included,
      excluded: plan.excluded,
      price: plan.prices?.[frequency] || '',
    }))
})

const addOnOptions = computed((): AddOn[] => {
  const frequency = form.value.selected_billing_frequency as FundBillingFrequency
  return _catalogFundAddOns.value
    .filter((addOn) => addOn.current)
    .map((addOn) => ({
      title: addOn.title,
      value: addOn.stripe_name,
      description: addOn.description,
      price: addOn.prices?.[frequency] || '',
    }))
})

////////////////////////////////////////////////////////////////
//// Form State & Validation
////////////////////////////////////////////////////////////////

const initialState = {
  // configuration fields
  is_venture360_the_administrator: true,
  is_venture360_forming_this_fund: false,
  raising_status: 'currently_raising' as string,

  // General Structure fields
  name: null,
  legal_name: null,
  fund_gp_name: null,
  admin_signer_id: null,
  admin_ids: [],
  management_fee_percentage: 0,
  carried_interest: 0,
  preferred_return: 0,
  currency: 'USD',
  tax_id: null,
  state_of_incorporation: 'DE',
  type: 'LLC',
  date: format(new Date(), 'yyyy-MM-dd'),

  // Address fields
  street1: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  county: '',
  country: 'US',

  // Investment Details fields (deal card)
  company_name: null,
  company_description: null,
  company_website: null,
  sectors: [] as string[],
  raised_amount: initialMoney,
  minimum_investment_amount: initialMoney,
  pre_money_valuation_amount: initialMoney,
  expected_close_date: null,
  type_of_investment: null,
  other_investment_type: null,
  fund_type: null as string | null,
  investment_stage: null as string | null,
  visibility: 'all_investors' as 'all_investors' | 'selected',
  invited_investor_ids: [] as string[],

  // Capital cadence & waterfall fields
  capital_call_frequency: null,
  ownership_type: 'capital_called',
  investment_period_end_date: null,
  initial_closing_date: null,
  has_interest_catch_up_provision: false,
  waterfall_calculation: null,

  // Disbursement Details fields
  is_requesting_bank_connection: true,
  disbursement_method: 'wire',
  disbursement_wire_bank_name: '',
  disbursement_wire_bank_address: '',
  disbursement_wire_bank_routing_number: '',
  disbursement_wire_bank_account_number: '',
  disbursement_wire_repeat_bank_account_number: '',
  disbursement_wire_bank_swift_code: '',
  disbursement_wire_account_name: '',
  disbursement_wire_for_further_credit_to: '',
  disbursement_wire_bank_notes: '',
  capital_call_wiring_instructions: null,

  // Tax Section fields
  gosystem_locator: null,
  is_filing_required: true,
  not_filing_reason: null,
  state_filing: null,
  basis: 'Cash',
  starting_tax_year: new Date().getFullYear(),
  ending_tax_year: null,

  selected_billing_frequency: 'year',
  selected_services: [],
}
const original_bank_account_number = ref('')
const isLoadingEntityData = ref(false)
const createInitialFormState = () => ({ ...initialState })
const resetFormState = () => {
  form.value = createInitialFormState()
  original_bank_account_number.value = ''
  isLoadingEntityData.value = false
}
const form = ref(createInitialFormState())
const check_bank_account_number = computed(
  () => original_bank_account_number.value !== form.value.disbursement_wire_bank_account_number,
)
const generateRules = (authStore, type: 'create' | 'edit') => ({
  is_venture360_the_administrator: { required },
  name: { required },
  legal_name: { required },
  admin_signer_id: {
    required: helpers.withMessage('Value is required', (value: any) => {
      if (authStore.is_site_admin) return true
      return !!value
    }),
  },
  admin_ids: {
    minLength: helpers.withMessage('Need at least 1 admin', (value: any) => !!value?.length || authStore.is_site_admin),
  },

  // Investment Details fields
  fund_type: {
    required: helpers.withMessage('Fund type is required', (value: any) => {
      if (!form.value.is_venture360_the_administrator) return true
      return !!value
    }),
  },

  // Disbursement Details fields
  is_requesting_bank_connection: {
    requiredIf: helpers.withMessage('Value is required', (value: any) =>
      type === 'edit' ? typeof value === 'boolean' : true,
    ),
  },
  disbursement_wire_repeat_bank_account_number: {
    sameAsRepeat: sameAs(
      computed(() =>
        check_bank_account_number.value
          ? form.value.disbursement_wire_bank_account_number
          : form.value.disbursement_wire_repeat_bank_account_number,
      ),
    ),
  },

  // subscription
  selected_services: {
    minLength: helpers.withMessage('Need at least 1 plan', (value: any) => {
      if (!form.value.is_venture360_the_administrator) return true
      const services = [...value].filter((service) => !addOnOptions.value.some((option) => option.value === service))
      return !!services.length
    }),
  },

  // Tax Section fields
  is_filing_required: {
    requiredIf: helpers.withMessage('Value is required', (value: any) => {
      if (!authStore.is_site_admin) return true
      if (!form.value.is_venture360_the_administrator) return true
      return typeof value === 'boolean'
    }),
  },
  tax_id: {
    minLength: helpers.withMessage('Must be at least 9 characters', (value: any) => {
      if (!value) return true
      if (form.value.country !== 'US') return true
      return value.toString().replace(/\D/g, '').length >= 9
    }),
  },
  not_filing_reason: {
    requiredIf: helpers.withMessage('Value is required', (value: any) => {
      if (!authStore.is_site_admin) return true
      if (!form.value.is_venture360_the_administrator) return true
      if (form.value.is_filing_required) return true
      return !!value
    }),
  },
})

////////////////////////////////////////////////////////////////
//// Computed
////////////////////////////////////////////////////////////////

const totalDue = computed((): Money => {
  let total = 0
  form.value.selected_services.forEach((service) => {
    const allPaymentOptions = [...subscriptionOptions.value, ...addOnOptions.value]
    const price = allPaymentOptions.find((option) => option.value === service)?.price

    if (price) {
      const priceNumber = parse(price, 'currency') as number
      total += priceNumber
    }
  })
  return toMoney(total, 'USD')
})

////////////////////////////////////////////////////////////////
//// Main
////////////////////////////////////////////////////////////////

const initializeFundForm = (investorStore, catalogStore, slug?: string) => async () => {
  const tasks: Promise<unknown>[] = [investorStore.fetchIndividuals('all'), investorStore.fetchInvestors()]
  if (slug) tasks.push(catalogStore.fetchFundCatalog(slug))
  await Promise.all(tasks)
}

const buildPayload = () => {
  const selected_services = form.value.is_venture360_the_administrator ? form.value.selected_services : []
  const lifecycle_state = form.value.raising_status === 'closed' ? 'closed' : 'raising'
  const payload = {
    id: null,
    entity_type: 'fund',
    lifecycle_state,
    ...form.value,
    investor_set_profile_attributes: {
      is_requesting_bank_connection: form.value.is_requesting_bank_connection,
    },
    deal_card_attributes: {
      name: form.value.name,
      description: form.value.company_description,
      website: form.value.company_website,
      sectors: form.value.sectors,
      investment_type: form.value.type_of_investment,
      other_investment_type: form.value.other_investment_type,
      target_raise_amount: toNumber(form.value.raised_amount),
      minimum_commitment_amount: toNumber(form.value.minimum_investment_amount),
      pre_money_valuation_amount: toNumber(form.value.pre_money_valuation_amount),
      expected_close_date: form.value.expected_close_date,
      currency: form.value.currency,
      visibility: form.value.visibility,
      invited_investor_ids: form.value.visibility === 'selected' ? form.value.invited_investor_ids : [],
      fund_type: form.value.fund_type,
      investment_stage: form.value.investment_stage,
    },
    selected_services,
  }
  delete payload.company_name
  delete payload.company_description
  delete payload.company_website
  delete payload.sectors
  delete payload.raised_amount
  delete payload.minimum_investment_amount
  delete payload.pre_money_valuation_amount
  delete payload.expected_close_date
  delete payload.type_of_investment
  delete payload.other_investment_type
  delete payload.fund_type
  delete payload.investment_stage
  delete payload.visibility
  delete payload.invited_investor_ids
  delete payload.raising_status
  delete payload.is_requesting_bank_connection

  return payload
}

const setFormWithEntityData = (entity: EntityExpanded) => {
  if (!entity) return
  isLoadingEntityData.value = true
  const profile = entity.investor_set_profile || ({} as any)
  const address = entity.address || ({} as any)

  form.value.raising_status = entity.lifecycle_state === 'raising' ? 'currently_raising' : 'closed'
  form.value.is_venture360_the_administrator = entity.is_venture360_the_administrator
  form.value.is_venture360_forming_this_fund = entity.is_venture360_forming_this_fund || false
  form.value.name = entity.name
  form.value.legal_name = entity.legal_name
  form.value.admin_signer_id = entity.admin_signer_id
  form.value.admin_ids = entity.admins?.map((admin) => admin.id) || []
  form.value.management_fee_percentage = entity.management_fee_percentage
  form.value.carried_interest = entity.carried_interest
  form.value.preferred_return = entity.preferred_return_percentage
  form.value.tax_id = entity.tax_id || null
  form.value.currency = entity.currency
  form.value.state_of_incorporation = entity.state_of_incorporation || null
  form.value.fund_gp_name = entity.fund_gp_name
  form.value.type = entity.type
  form.value.date = entity.date_established

  form.value.street1 = address.line1 || ''
  form.value.street2 = address.line2 || ''
  form.value.city = address.city || ''
  form.value.state = address.state || ''
  form.value.zip = address.zip || ''
  form.value.county = address.county || ''
  form.value.country = address.country || 'US'

  form.value.is_requesting_bank_connection = profile.is_requesting_bank_connection

  form.value.disbursement_method = 'wire'
  form.value.disbursement_wire_bank_name = entity.disbursement_wire_bank_name
  form.value.disbursement_wire_bank_address = entity.disbursement_wire_bank_address
  form.value.disbursement_wire_bank_routing_number = entity.disbursement_wire_bank_routing_number
  form.value.disbursement_wire_bank_account_number = entity.disbursement_wire_bank_account_number
  form.value.disbursement_wire_bank_swift_code = entity.disbursement_wire_bank_swift_code
  form.value.disbursement_wire_account_name = entity.disbursement_wire_account_name
  form.value.disbursement_wire_for_further_credit_to = entity.disbursement_wire_for_further_credit_to
  form.value.disbursement_wire_bank_notes = entity.disbursement_wire_bank_notes
  original_bank_account_number.value = entity.disbursement_wire_bank_account_number
  form.value.capital_call_wiring_instructions = entity.capital_call_wiring_instructions

  form.value.capital_call_frequency = entity.capital_call_frequency
  form.value.ownership_type = entity.ownership_type
  form.value.investment_period_end_date = entity.investment_period_end_date
  form.value.initial_closing_date = entity.initial_closing_date
  form.value.has_interest_catch_up_provision = entity.has_interest_catch_up_provision
  form.value.waterfall_calculation = entity.waterfall_calculation

  // Investment details (from deal card)
  // Note: deal_card.name maps to form.name (fund name), not company_name (management company)
  const dealCard = entity.deal_card
  if (dealCard) {
    form.value.company_description = dealCard.description || null
    form.value.company_website = dealCard.website || null
    form.value.sectors = dealCard.sectors || []
    form.value.type_of_investment = dealCard.investment_type || null
    form.value.other_investment_type = dealCard.other_investment_type || null
    form.value.raised_amount = dealCard.target_raise
      ? toMoney(dealCard.target_raise.amount, dealCard.target_raise.currency)
      : initialMoney
    form.value.minimum_investment_amount = dealCard.minimum_commitment
      ? toMoney(dealCard.minimum_commitment.amount, dealCard.minimum_commitment.currency)
      : initialMoney
    form.value.pre_money_valuation_amount = dealCard.pre_money_valuation
      ? toMoney(dealCard.pre_money_valuation.amount, dealCard.pre_money_valuation.currency)
      : initialMoney
    form.value.expected_close_date = dealCard.expected_close_date || null
    form.value.visibility = dealCard.visibility || 'all_investors'
    form.value.invited_investor_ids = dealCard.invited_investor_ids || []
    form.value.fund_type = dealCard.fund_type || null
    form.value.investment_stage = dealCard.investment_stage || null
  }

  // Tax Section fields
  form.value.gosystem_locator = entity.gosystem_locator
  form.value.is_filing_required = entity.is_filing_required
  form.value.not_filing_reason = entity.not_filing_reason
  form.value.state_filing = entity.state_filing
  form.value.basis = entity.basis
  form.value.starting_tax_year = entity.starting_tax_year || null
  form.value.ending_tax_year = entity.ending_tax_year || null

  // Subscription Section fields
  form.value.selected_billing_frequency = entity.selected_billing_frequency
  form.value.selected_services = entity.selected_services || []

  nextTick(() => {
    isLoadingEntityData.value = false
  })
}

export const useFundForm = (type: 'create' | 'edit', slug?: string) => {
  const authStore = useAuthStore()
  const investorStore = useInvestingInvestorStore()
  const catalogStore = useCatalogStore()

  const initialize_fund_form = initializeFundForm(investorStore, catalogStore, slug)
  const rules = generateRules(authStore, type)
  const v$ = useVuelidate(rules, form, { $lazy: true })
  const adminOptions = generateAdminOptions(investorStore)
  const managerOptions = generateManagersOptions(investorStore)
  const investorOptions = computed(() =>
    investorStore.individuals
      .map((investor) => ({
        label: investor.name,
        value: investor.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  )

  // Sync catalog store data to module-level refs so option computeds resolve reactively.
  watch(
    () => catalogStore.fundPlans,
    (val) => {
      _catalogFundPlans.value = val as CatalogFundPlan[]
    },
    { immediate: true },
  )
  watch(
    () => catalogStore.fundAddOns,
    (val) => {
      _catalogFundAddOns.value = val as CatalogFundAddOn[]
    },
    { immediate: true },
  )

  watch(
    () => form.value.date,
    (newDate) => {
      if (isLoadingEntityData.value) return
      if (!newDate) return
      form.value.starting_tax_year = new Date(newDate).getFullYear()
    },
    { immediate: true },
  )

  return {
    initializeFundForm: initialize_fund_form,
    adminOptions,
    managerOptions,
    capitalCallFrequencyOptions,
    ownershipTypeOptions,
    selectedBillingFrequencyOptions,
    investmentTypeOptions,
    sectorOptions,
    visibilityOptions,
    investmentStageOptions,
    investorOptions,
    form,
    v$,
    subscriptionOptions,
    addOnOptions,
    totalDue,
    buildPayload,
    setFormWithEntityData,
    check_bank_account_number,
    legalStructureOptions,
    resetFormState,
  }
}
